import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import { useApi } from '@hooks/useApi';
import { useAsync } from 'react-use';
import { Z_INDEX_THRESHOLD, Z_INDEX_OFFSET } from '@features/dashboard/layoutUtils';
import { debounce } from 'lodash';
import { useUpdateUserSettings } from './UserContextProvider';

type ZIndexAction =
  | { type: 'INITIALIZE'; payload: Record<string, number> }
  | { type: 'UPDATE_WIDGET'; payload: { id: string; zIndex: number } }
  | { type: 'NORMALIZE' };

type ZIndexState = {
  zIndices: Record<string, number>;
  updateWidgetZIndex: (id: string, zIndex: number) => void;
  normaliseZIndices: () => void;
  getHighestZIndex: () => number;
  loading: boolean;
};

function zIndexReducer(state: Record<string, number>, action: ZIndexAction): Record<string, number> {
  switch (action.type) {
    case 'INITIALIZE':
      return action.payload;

    case 'UPDATE_WIDGET': {
      return { ...state, [action.payload.id]: action.payload.zIndex };
    }

    case 'NORMALIZE': {
      const values = Object.values(state);
      if (Math.max(...values) < Z_INDEX_THRESHOLD) {
        return state;
      }

      const sortedIndices = [...new Set(values)].sort((a, b) => a - b);
      const indexMap = new Map(sortedIndices.map((oldIndex, i) => [oldIndex, Z_INDEX_OFFSET + i]));

      return Object.fromEntries(Object.entries(state).map(([id, oldIndex]) => [id, indexMap.get(oldIndex) || Z_INDEX_OFFSET]));
    }
  }
}

const ZIndexContext = createContext<ZIndexState | undefined>(undefined);
ZIndexContext.displayName = 'ZIndexContext';

export function ZIndexProvider({ children }: { children: React.ReactNode }) {
  const { apiClient } = useApi();
  const [zIndices, dispatch] = useReducer(zIndexReducer, {});
  const updateUserSettings = useUpdateUserSettings();

  const userSettings = useAsync(async () => {
    if (!apiClient) return null;
    return await apiClient.getUserSettings();
  }, [apiClient]);

  useEffect(() => {
    if (!userSettings.loading && userSettings.value) {
      const savedZIndices = userSettings.value.dashboard?.widgetZIndices;
      if (savedZIndices) {
        dispatch({ type: 'INITIALIZE', payload: savedZIndices });
      }
    }
  }, [userSettings]);

  const saveZIndicesToUserSettings = useCallback(
    debounce(async (newZIndices: Record<string, number>) => {
      if (!apiClient) return;

      try {
        const currentUserSettings = await apiClient.getUserSettings();

        await updateUserSettings?.({
          ...currentUserSettings,
          dashboard: {
            ...(currentUserSettings.dashboard || {}),
            widgetZIndices: newZIndices,
          },
        });
      } catch (error) {
        console.error('Failed to save z-indices to user settings:', error);
      }
    }, 1000),
    [apiClient, updateUserSettings]
  );

  const updateWidgetZIndex = useCallback(
    (id: string, zIndex: number) => {
      dispatch({ type: 'UPDATE_WIDGET', payload: { id, zIndex } });

      const newZIndices = { ...zIndices, [id]: zIndex };
      saveZIndicesToUserSettings(newZIndices);
    },
    [zIndices, saveZIndicesToUserSettings]
  );

  const normaliseZIndices = useCallback(() => {
    dispatch({ type: 'NORMALIZE' });

    const normalizedZIndices = zIndexReducer(zIndices, { type: 'NORMALIZE' });
    saveZIndicesToUserSettings(normalizedZIndices);
  }, [zIndices, saveZIndicesToUserSettings]);

  const getHighestZIndex = useCallback(() => {
    return Math.max(...Object.values(zIndices), 20);
  }, [zIndices]);

  const value: ZIndexState = {
    zIndices,
    updateWidgetZIndex,
    normaliseZIndices,
    getHighestZIndex,
    loading: userSettings.loading,
  };

  return <ZIndexContext.Provider value={value}>{children}</ZIndexContext.Provider>;
}

export function useZIndexContext(): ZIndexState {
  const context = useContext(ZIndexContext);
  if (!context) {
    throw new Error('useZIndexContext must be used within ZIndexProvider');
  }
  return context;
}
